import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';


const LoginForm: React.FC = () => {
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [showVerifyAccount, setShowVerifyAccount] = useState(false);
  const [verifyIdentifier, setVerifyIdentifier] = useState('');
  const [verifyOtp, setVerifyOtp] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState('');
  const [restorePrompt, setRestorePrompt] = useState<{
    identifier: string;
    password: string;
    message: string;
    userId?: string;
    deletedDate?: string;
    restoreUntil?: string;
  } | null>(null);
  const [restoreOtpRequested, setRestoreOtpRequested] = useState(false);
  const [restoreOtp, setRestoreOtp] = useState('');
  const [restoreMessage, setRestoreMessage] = useState('');
  const [restoreLoading, setRestoreLoading] = useState(false);
  const {
    login,
    requestRestoreAccountOtp,
    restoreDeletedAccount,
    verifyRegistrationOtp,
    resendRegistrationOtp,
    requestForgotPasswordOtp,
    resetPasswordWithOtp,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requestedNextPath = params.get('next') || '';
  const nextPath = requestedNextPath.startsWith('/') && !requestedNextPath.startsWith('//') ? requestedNextPath : '/dashboard';
  const registerPath = nextPath !== '/dashboard' ? `/register?next=${encodeURIComponent(nextPath)}` : '/register';

  const resolveUiError = (err: any, fallback: string): string => {
    const payload = err?.response?.data;
    const message = payload?.message;
    if (typeof err?.message === 'string' && err.message.trim() && !err.message.includes('status code')) {
      return err.message;
    }
    if (typeof message === 'string' && message.trim()) return message;
    if (message && typeof message === 'object' && typeof message.message === 'string') {
      return message.message;
    }
    if (typeof payload?.error === 'string' && payload.error.trim()) return payload.error;
    return fallback;
  };

  const formatRestoreDate = (value?: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRestorePrompt(null);
    setRestoreOtpRequested(false);
    setRestoreOtp('');
    setRestoreMessage('');
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setRestorePrompt(null);
    setLoading(true);
    
    try {
      await login(formData.identifier, formData.password);
      navigate(nextPath);
    } catch (err: any) {
      const message = err?.message || 'Failed to log in. Please check your credentials.';
      if (err?.restoreAvailable || err?.code === 'ACCOUNT_DELETED_RESTORE_AVAILABLE') {
        setRestorePrompt({
          identifier: err?.identifier || formData.identifier.trim(),
          password: formData.password,
          message,
          userId: err?.userId,
          deletedDate: err?.deletedDate,
          restoreUntil: err?.restoreUntil,
        });
        return;
      }
      setError(message);
      if (String(message).toLowerCase().includes('not verified')) {
        setShowVerifyAccount(true);
        setVerifyIdentifier(formData.identifier.trim());
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestRestoreOtp = async () => {
    if (!restorePrompt) return;
    try {
      setRestoreLoading(true);
      setError('');
      setRestoreMessage('');
      const response = await requestRestoreAccountOtp(restorePrompt.identifier, restorePrompt.password);
      const otpNote = response?.otp ? ` Dev OTP: ${response.otp}` : '';
      const deliveryNote = response?.otpDeliveryTarget ? ` (${response.otpDeliveryTarget})` : '';
      setRestorePrompt((current) => current ? {
        ...current,
        userId: response?.userId || current.userId,
        deletedDate: response?.deletedDate || current.deletedDate,
        restoreUntil: response?.restoreUntil || current.restoreUntil,
      } : current);
      setRestoreOtpRequested(true);
      setRestoreMessage(`${response?.message || 'We sent an OTP to your registered mobile number.'}${deliveryNote}${otpNote}`);
    } catch (err: any) {
      setError(resolveUiError(err, 'Unable to send restore OTP right now.'));
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleRestoreDeletedAccount = async () => {
    if (!restorePrompt) return;
    if (!restorePrompt.userId) {
      setRestoreMessage('Request a restore OTP first.');
      return;
    }
    if (!restoreOtp.trim()) {
      setRestoreMessage('Enter the OTP sent to your registered mobile number.');
      return;
    }
    try {
      setRestoreLoading(true);
      setError('');
      await restoreDeletedAccount(restorePrompt.userId, restoreOtp.trim());
      setRestorePrompt(null);
      setRestoreOtpRequested(false);
      setRestoreOtp('');
      setRestoreMessage('');
      navigate(nextPath);
    } catch (err: any) {
      setRestoreMessage(resolveUiError(err, 'Unable to restore this account right now.'));
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleSendVerificationOtp = async () => {
    if (!verifyIdentifier.trim()) {
      setVerifyMessage('Enter email, username or phone to receive OTP.');
      return;
    }
    try {
      setVerifyLoading(true);
      const response = await resendRegistrationOtp(verifyIdentifier.trim());
      const otpNote = response?.otp ? ` Dev OTP: ${response.otp}` : '';
      setVerifyMessage(`${response?.message || 'OTP sent for verification.'}${otpNote}`);
    } catch (err: any) {
      setVerifyMessage(resolveUiError(err, 'Failed to send verification OTP.'));
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleVerifyAccount = async () => {
    if (!verifyIdentifier.trim() || !verifyOtp.trim()) {
      setVerifyMessage('Identifier and OTP are required.');
      return;
    }
    try {
      setVerifyLoading(true);
      await verifyRegistrationOtp(verifyIdentifier.trim(), verifyOtp.trim());
      navigate(nextPath);
    } catch (err: any) {
      setVerifyMessage(resolveUiError(err, 'Failed to verify account.'));
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleRequestForgotOtp = async () => {
    if (!forgotIdentifier.trim()) {
      setForgotMessage('Enter email, username or phone.');
      return;
    }
    try {
      setForgotLoading(true);
      const response = await requestForgotPasswordOtp(forgotIdentifier.trim());
      setOtpRequested(true);
      const otpNote = response?.otp ? ` Dev OTP: ${response.otp}` : '';
      setForgotMessage(`${response?.message || 'OTP sent.'}${otpNote}`);
    } catch (err: any) {
      setForgotMessage(err?.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!forgotIdentifier.trim() || !forgotOtp.trim() || !newPassword.trim()) {
      setForgotMessage('Identifier, OTP and new password are required.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setForgotMessage('Passwords do not match.');
      return;
    }
    try {
      setForgotLoading(true);
      const response = await resetPasswordWithOtp(
        forgotIdentifier.trim(),
        forgotOtp.trim(),
        newPassword.trim(),
      );
      setForgotMessage(response?.message || 'Password reset successful. Please log in.');
      setShowForgotPassword(false);
      setOtpRequested(false);
      setForgotOtp('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: any) {
      setForgotMessage(err?.response?.data?.message || 'Failed to reset password.');
    } finally {
      setForgotLoading(false);
    }
  };

  
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(79,70,229,0.16),_transparent_26%),radial-gradient(circle_at_bottom_right,_rgba(16,185,129,0.14),_transparent_22%),linear-gradient(180deg,_#f8fafc_0%,_#e0e7ff_100%)]">
      <div className="flex min-h-[calc(100vh-4rem)]">
        {/* Left side - Background Image */}
        <div className="hidden lg:block relative w-1/2">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1503676260728-1c00da094a05?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80)',
              backgroundPosition: 'center',
              backgroundSize: 'cover',
            }}
          >
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(15,23,42,0.45)_0%,rgba(49,46,129,0.82)_100%)]"></div>
            <div className="relative h-full flex flex-col justify-center p-12 text-white">
              <span className="inline-flex w-fit rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-indigo-100">Member Access</span>
              <h2 className="mt-6 text-5xl font-semibold tracking-tight">Welcome back to LocalAlly</h2>
              <p className="mt-4 max-w-xl text-lg text-indigo-100">Pick up where you left off, manage your classes, and stay close to the students and teachers around you.</p>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white bg-opacity-20 p-2 rounded-full">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium">Secure Access</h3>
                    <p className="mt-1 text-indigo-100">Your data is always protected with us.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white bg-opacity-20 p-2 rounded-full">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium">24/7 Access</h3>
                    <p className="mt-1 text-indigo-100">Learn at your own pace, anytime, anywhere.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white bg-opacity-20 p-2 rounded-full">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium">Community</h3>
                    <p className="mt-1 text-indigo-100">Join thousands of learners worldwide.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right side - Login Form */}
        <div className="flex w-full items-start justify-center px-4 py-6 sm:px-6 sm:py-10 lg:w-1/2 lg:px-12">
      <div className="w-full max-w-lg space-y-8 rounded-[26px] border border-white/60 bg-white/88 p-4 shadow-[0_24px_80px_rgba(15,23,42,0.10)] backdrop-blur sm:rounded-[30px] sm:p-8">
        <div>
          <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-600">
            Secure Sign In
          </div>
          <h2 className="mt-4 text-[1.85rem] font-semibold tracking-tight text-slate-900 sm:mt-6 sm:text-4xl">
            {showForgotPassword ? 'Reset your password' : 'Sign in to your account'}
          </h2>
          {!showForgotPassword ? (
            <p className="mt-2 text-sm text-slate-600">
              Or{' '}
              <Link to={registerPath} className="font-medium text-indigo-600 hover:text-indigo-500">
                create a new account
              </Link>
            </p>
          ) : null}
        </div>
        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        {restorePrompt && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
            <p className="font-semibold">Your LocalAlly account was deleted.</p>
            <p className="mt-1">{restorePrompt.message}</p>
            {(restorePrompt.deletedDate || restorePrompt.restoreUntil) && (
              <p className="mt-2 text-xs text-amber-800">
                {restorePrompt.deletedDate ? `Deleted on ${formatRestoreDate(restorePrompt.deletedDate)}. ` : ''}
                {restorePrompt.restoreUntil ? `Restore available until ${formatRestoreDate(restorePrompt.restoreUntil)}.` : ''}
              </p>
            )}
            {!restoreOtpRequested ? (
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={handleRequestRestoreOtp}
                  disabled={restoreLoading}
                  className="inline-flex items-center justify-center rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {restoreLoading ? 'Sending OTP...' : 'Restore Account'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRestorePrompt(null);
                    setRestoreOtpRequested(false);
                    setRestoreOtp('');
                    setRestoreMessage('');
                  }}
                  disabled={restoreLoading}
                  className="inline-flex items-center justify-center rounded-full border border-amber-300 bg-white px-4 py-2 text-xs font-semibold text-amber-900 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                <input
                  type="text"
                  value={restoreOtp}
                  onChange={(event) => setRestoreOtp(event.target.value)}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter OTP"
                  className="block w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-amber-400"
                />
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleRestoreDeletedAccount}
                    disabled={restoreLoading}
                    className="inline-flex items-center justify-center rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {restoreLoading ? 'Restoring...' : 'Verify & Restore'}
                  </button>
                  <button
                    type="button"
                    onClick={handleRequestRestoreOtp}
                    disabled={restoreLoading}
                    className="inline-flex items-center justify-center rounded-full border border-amber-300 bg-white px-4 py-2 text-xs font-semibold text-amber-900 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Resend OTP
                  </button>
                </div>
              </div>
            )}
            {restoreMessage ? <p className="mt-2 text-xs text-amber-900">{restoreMessage}</p> : null}
          </div>
        )}
        {!showForgotPassword ? (
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="identifier" className="mb-2 block text-sm font-medium text-slate-700">
                Email / Username / Phone
              </label>
              <input
                id="identifier"
                name="identifier"
                type="text"
                autoComplete="username"
                required
                className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Email, username or phone"
                value={formData.identifier}
                onChange={handleChange}
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 pr-10 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 3l18 18" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10.58 10.58A2 2 0 0012 14a2 2 0 001.42-.58" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9.88 4.24A10.94 10.94 0 0112 4c5 0 9.27 3.11 11 8-0.61 1.72-1.68 3.23-3.08 4.4M6.1 6.1C4.36 7.36 3 9.07 2 12c1.73 4.89 6 8 10 8 1.61 0 3.15-.41 4.5-1.13" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 12s3.5-8 10-8 10 8 10 8-3.5 8-10 8-10-8-10-8z" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <button 
                type="button" 
                onClick={() => {
                  setShowForgotPassword(true);
                  setForgotMessage('');
                }}
                className="font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative flex w-full justify-center rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_35px_rgba(15,23,42,0.18)] transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          {showVerifyAccount && (
            <div className="space-y-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-medium text-amber-800">Verify your account with OTP</p>
              <input
                type="text"
                value={verifyIdentifier}
                onChange={(e) => setVerifyIdentifier(e.target.value)}
                placeholder="Email, username or phone"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={handleSendVerificationOtp}
                  disabled={verifyLoading}
                  className="rounded-md bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  {verifyLoading ? 'Sending...' : 'Send OTP'}
                </button>
                <input
                  type="text"
                  value={verifyOtp}
                  onChange={(e) => setVerifyOtp(e.target.value)}
                  placeholder="Enter OTP"
                  className="block w-full flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={handleVerifyAccount}
                  disabled={verifyLoading}
                  className="rounded-md bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {verifyLoading ? 'Verifying...' : 'Verify'}
                </button>
              </div>
              {verifyMessage ? <p className="text-xs text-amber-900">{verifyMessage}</p> : null}
            </div>
          )}
        </form>
        ) : (
          <div className="mt-8 space-y-4 rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
            <p className="text-sm font-medium text-indigo-800">Reset Password With OTP</p>
            <input
              type="text"
              value={forgotIdentifier}
              onChange={(e) => setForgotIdentifier(e.target.value)}
              placeholder="Email, username or phone"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={handleRequestForgotOtp}
              disabled={forgotLoading}
              className="w-full rounded-md bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-60 sm:w-auto"
            >
              {forgotLoading ? 'Sending...' : 'Send OTP'}
            </button>
            {otpRequested ? (
              <>
                <input
                  type="text"
                  value={forgotOtp}
                  onChange={(e) => setForgotOtp(e.target.value)}
                  placeholder="Enter OTP"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={forgotLoading}
                  className="w-full rounded-md bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60 sm:w-auto"
                >
                  {forgotLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </>
            ) : null}
            {forgotMessage ? <p className="text-xs text-indigo-900">{forgotMessage}</p> : null}
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setForgotMessage('');
                setOtpRequested(false);
              }}
              className="text-sm font-medium text-indigo-700 hover:text-indigo-900"
            >
              Back to login
            </button>
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
